/**
 * initYMaps.js
 *
 * Initializes the shared Y.Map structures for referenceMarks and comments
 * inside the collaborative Y.Doc. These maps are the single source of truth for
 * all document-associated metadata, replacing legacy RTDB.
 *
 * Map layout inside Y.Doc:
 *
 *   Y.Map("referenceMarks")
 *     └── [referenceMarkId]: Y.Map
 *           ├── addedReferences: Y.Map    ← client writes
 *           │     └── [localId]: Y.Map    ← individual reference data
 *           ├── suggestedReferences: Y.Map ← backend writes
 *           │     └── [localId]: Y.Map    ← AI-suggested citations
 *           ├── claims: Y.Map             ← backend writes
 *           │     └── [claimId]: Y.Map    ← individual claim data
 *           └── _meta: Y.Map              ← backend writes (status, hashes, summary)
 *
 *   Y.Map("comments")
 *     └── [threadId]: Y.Map              ← threadId = "{elementName}:{elementId}"
 *           └── [messageId]: Y.Map       ← individual message data
 *
 *   Y.Map("sourcePackage")
 *     ├── folders: Y.Map                 ← folder metadata keyed by folderId
 *     └── items:   Y.Map                 ← source items keyed by itemId
 *
 *   Y.Map("sitrepGenerations")
 *     └── [requestId]: Y.Map             ← see sitrepGenerationsYMap.js for the full field list
 *
 * All maps live at the root of the Y.Doc alongside "prosemirror" and "documentMeta".
 * Calling getMap() multiple times with the same name always returns the same Y.Map
 * instance, so this function is safe to call more than once.
 */

/**
 * Initialize Y.Maps for referenceMarks and comments on the provided Y.Doc.
 *
 * @param {import('yjs').Doc} ydoc - The shared Yjs document
 * @returns {{
 *   yReferenceMarks:  import('yjs').Map,
 *   yComments:        import('yjs').Map,
 *   ySourcePackage:   import('yjs').Map,
 *   yAgenticSections: import('yjs').Map,
 *   yAnnotations:     import('yjs').Map,
 *   yMapPreviews:     import('yjs').Map,
 *   yMapSitrepGenerations: import('yjs').Map,
 * }}
 */
export function initYMaps(ydoc) {
    if (!ydoc) {
        throw new Error('[initYMaps] ydoc must be provided');
    }

    const yReferenceMarks = ydoc.getMap('referenceMarks');
    const yComments = ydoc.getMap('comments');
    const ySourcePackage = ydoc.getMap('sourcePackage');
    const yAgenticSections = ydoc.getMap('agenticSections');
    const yAnnotations = ydoc.getMap('annotations');
    const yMapPreviews = ydoc.getMap('mapPreviews');
    const yMapSitrepGenerations = ydoc.getMap('sitrepGenerations');

    return {
        yReferenceMarks, yComments, ySourcePackage, yAgenticSections, yAnnotations, yMapPreviews,
        yMapSitrepGenerations,
    };
}
